import { injectable } from 'inversify';
import { NavigateFunction } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  ConneqtRepository,
  PlayerRepository,
  TeamRepository,
} from '../../../state/reduxRepositories';
import { AddPlayerViewService } from '../addPlayer.view.service';
import { PlayerCreatePayload } from '../../../state/reduxRepositories/players/player.interfaces';

// This is used when user adds an email when creating a player.
// This opens up a second modal to confirm that you want to send a conneqt request to the email for the player just created.
// It routes the user to the conneqt page after conneqtion is sent. Currently only orgs can send conneqt requests on player creation.
// For bulk player uploads there is no modal presented to the user, the conneqt request is sent if an email is provided.
@injectable()
export class ConneqtOnAddPlayerDomain {
  constructor(
    private conneqtRepository: ConneqtRepository,
    private playerRepository: PlayerRepository,
    private teamRepository: TeamRepository,
    private addPlayerViewService: AddPlayerViewService,
  ) {}

  useHasPendingConneqtInfoSelector = () => {
    return this.addPlayerViewService.useHasPendingConneqtInfoSelector();
  };

  useDispatchClearViewServices = () => {
    return this.addPlayerViewService.dispatchClearView();
  };

  dispatchBulkUploadCreatePlayer = (
    newPlayer: PlayerCreatePayload,
    emailToConneqtWith: string,
  ): boolean => {
    this.playerRepository
      .dispatchCreateAsync(newPlayer)
      .then((playerId: string): boolean => {
        if (emailToConneqtWith) {
          this.dispatchCreateConneqtForBulkUploadPlayer(
            newPlayer,
            playerId,
            emailToConneqtWith,
          );
        }
        return true;
      })
      .catch((error): boolean => {
        console.error(error);
        return false;
      });
    return true;
  };

  dispatchCreatePlayer = (
    newPlayer: PlayerCreatePayload,
    navigator: NavigateFunction,
    emailToConneqtWith?: string,
  ) => {
    this.playerRepository
      .dispatchCreateAsync(newPlayer)
      .then((playerId) => {
        if (emailToConneqtWith) {
          this.dispatchPendingConneqtInfo(playerId, emailToConneqtWith);
        }
      })
      .catch((error) => {
        console.error(error);
      });
  };

  private dispatchPendingConneqtInfo = (
    playerIdToConneqt: string,
    emailToConneqtWith?: string,
  ) => {
    this.addPlayerViewService.dispatchPendingConneqtInfo(
      playerIdToConneqt,
      emailToConneqtWith,
    );
  };

  dispatchClearPendingConneqtInfo = () => {
    this.addPlayerViewService.dispatchClearView();
  };

  dispatchCreateConneqtForPlayer = () => {
    const { playerIdToConneqt, emailToConneqtWith } =
      this.addPlayerViewService.viewStateSelector();

    const newPlayer = this.playerRepository.getPlayer(
      playerIdToConneqt as string,
    );

    if (!newPlayer || !emailToConneqtWith) {
      console.error(
        'You have to set player and email before calling dispatchCreateConneqtForPlayer',
      );
      return;
    }
    this.conneqtRepository.dispatchCreate({
      requestPlayerId: newPlayer.id,
      requestPlayerClues: `${newPlayer.firstName} ${newPlayer.lastName},${dayjs(
        newPlayer.dateOfBirth,
      ).format('DD/MM/YYYY')}`,
      responseEmail: emailToConneqtWith, // do we have to factor in multiple guardian emails present and sending conneqt invite to each of them?
    });

    this.addPlayerViewService.dispatchClearView();
  };

  dispatchCreateConneqtForBulkUploadPlayer = (
    newPlayer: PlayerCreatePayload,
    playerIdToConneqt: string | undefined,
    emailToConneqtWith: string | undefined,
  ) => {
    if (!newPlayer || !emailToConneqtWith) {
      console.error(
        'You have to set player and email before calling dispatchCreateConneqtForBulkUploadPlayer',
      );
      return;
    }
    this.conneqtRepository.dispatchCreate({
      requestPlayerId: playerIdToConneqt,
      requestPlayerClues: `${newPlayer.firstName} ${newPlayer.lastName},${dayjs(
        newPlayer.dateOfBirth,
      ).format('DD/MM/YYYY')}`,
      responseEmail: emailToConneqtWith, // do we have to factor in multiple guardian emails present and sending conneqt invite to each of them?
    });

    this.addPlayerViewService.dispatchClearView();
  };
}
